import React, { useState, useEffect } from "react";
import {
  getAuthorizationUrl,
  getClientToken,
  refreshClientToken,
} from "../services/ebayService";

/**
 * Component for generating an eBay client access token via user consent flow
 * @param {Object} props - Component props
 * @param {Function} props.onTokenUpdate - Callback function when token is updated
 */
function ClientTokenGenerator({ onTokenUpdate }) {
  // State for credential inputs
  const [clientId, setClientId] = useState(
    process.env.REACT_APP_EBAY_CLIENT_ID || ""
  );
  const [clientSecret, setClientSecret] = useState("");
  const [ruName, setRuName] = useState("");
  const [devId, setDevId] = useState("");
  const [certId, setCertId] = useState("");
  const [refreshToken, setRefreshToken] = useState("");
  const [authCode, setAuthCode] = useState("");
  const [sessionId, setSessionId] = useState("");

  // State for token info and UI state
  const [tokenInfo, setTokenInfo] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isAuthorizing, setIsAuthorizing] = useState(false);
  const [activeTab, setActiveTab] = useState("newToken"); // newToken or refreshToken

  // Check for stored session ID and load env vars on component mount
  useEffect(() => {
    // Check for stored session ID
    const storedSessionId = sessionStorage.getItem("ebay_session_id");
    if (storedSessionId) {
      setSessionId(storedSessionId);
    }

    // Load environment credentials - clientId is already initialized with env variable
    if (process.env.REACT_APP_EBAY_CLIENT_SECRET) {
      setClientSecret(process.env.REACT_APP_EBAY_CLIENT_SECRET);
    }
    if (process.env.REACT_APP_EBAY_RUNAME) {
      setRuName(process.env.REACT_APP_EBAY_RUNAME);
    }
    if (process.env.REACT_APP_EBAY_DEV_ID) {
      setDevId(process.env.REACT_APP_EBAY_DEV_ID);
    }
    if (process.env.REACT_APP_EBAY_CERT_ID) {
      setCertId(process.env.REACT_APP_EBAY_CERT_ID);
    }
  }, []);

  // Handle redirect to eBay for authorization
  const handleAuthorize = async () => {
    try {
      setIsAuthorizing(true);
      setError(null);
      setTokenInfo(null);

      if (!clientId) {
        setError("Client ID is required");
        setIsAuthorizing(false);
        return;
      }

      if (!ruName) {
        setError(
          "RuName is required. You need to register a return URL in your eBay developer account."
        );
        setIsAuthorizing(false);
        return;
      }

      if (!devId) {
        setError("Dev ID is required for the Trading API.");
        setIsAuthorizing(false);
        return;
      }

      if (!certId) {
        setError("Cert ID is required for the Trading API.");
        setIsAuthorizing(false);
        return;
      }

      // Generate the authorization URL and redirect
      const authUrl = await getAuthorizationUrl({
        clientId,
        ruName,
        devId,
        certId,
      });
      console.log("Redirecting to:", authUrl);

      // Load session ID from storage
      const storedSessionId = sessionStorage.getItem("ebay_session_id");
      if (storedSessionId) {
        setSessionId(storedSessionId);
      }

      // Open in a new window or redirect the current window
      window.open(authUrl, "_blank");

      // Clear any previous errors
      setError(null);
    } catch (err) {
      setError(err.message || "Failed to generate authorization URL");
      console.error("Error generating auth URL:", err);
    } finally {
      setIsAuthorizing(false);
    }
  };

  // Get token using the Trading API approach (matching Java implementation)
  const handleGetToken = async () => {
    setIsLoading(true);
    setTokenInfo(null);
    setError(null);

    try {
      // Check if we have a stored session ID
      const effectiveSessionId =
        sessionId || sessionStorage.getItem("ebay_session_id");

      if (!effectiveSessionId) {
        throw new Error(
          "No session ID found. Please authorize with eBay first."
        );
      }

      if (!clientId || !devId || !certId) {
        throw new Error("Client ID, Dev ID, and Cert ID are required");
      }

      // Call the client token function with the session ID
      const tokenData = await getClientToken({
        sessionId: effectiveSessionId,
        clientId,
        devId,
        certId,
      });

      setTokenInfo(tokenData);
      console.log("Client token successfully obtained:", tokenData);

      // Call the onTokenUpdate callback if provided
      if (onTokenUpdate) {
        onTokenUpdate(tokenData);
      }

      // Display a success message
      setError(null);
    } catch (err) {
      setError(err.message || "Failed to get client token");
      console.error("Error getting client token:", err);
    } finally {
      setIsLoading(false);
    }
  };

  // Refresh an existing token
  const handleRefreshToken = async () => {
    setIsLoading(true);
    setTokenInfo(null);
    setError(null);

    try {
      if (!refreshToken || !clientId || !clientSecret) {
        throw new Error(
          "Refresh token, Client ID and Client Secret are required"
        );
      }

      // Call the refresh token function
      const tokenData = await refreshClientToken({
        refreshToken,
        clientId,
        clientSecret,
      });

      setTokenInfo(tokenData);
      console.log("Client token successfully refreshed:", tokenData);
    } catch (err) {
      setError(err.message || "Failed to refresh client token");
      console.error("Error refreshing client token:", err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-md overflow-hidden p-6">
      <h2 className="text-2xl font-bold text-gray-900 mb-4">
        eBay Client Token Generator
      </h2>
      <p className="text-gray-600 mb-6">
        This tool helps you get an eBay client access token through the user
        consent flow. This token has higher privileges than the application
        token and can access your eBay account data.
      </p>

      <div className="flex border-b border-gray-200 mb-6">
        <button
          className={`py-2 px-4 mr-2 font-medium text-sm focus:outline-none ${
            activeTab === "newToken"
              ? "text-blue-600 border-b-2 border-blue-600"
              : "text-gray-500 hover:text-gray-700"
          }`}
          onClick={() => setActiveTab("newToken")}
        >
          New Token
        </button>
        <button
          className={`py-2 px-4 font-medium text-sm focus:outline-none ${
            activeTab === "refreshToken"
              ? "text-blue-600 border-b-2 border-blue-600"
              : "text-gray-500 hover:text-gray-700"
          }`}
          onClick={() => setActiveTab("refreshToken")}
        >
          Refresh Token
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
          {error}
        </div>
      )}

      {activeTab === "newToken" ? (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Client ID
              </label>
              <input
                type="text"
                value={clientId}
                onChange={(e) => {
                  // Only allow changing if env variable is not set
                  if (!process.env.REACT_APP_EBAY_CLIENT_ID) {
                    setClientId(e.target.value);
                  }
                }}
                placeholder="Your eBay Client ID"
                className={`w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm ${
                  process.env.REACT_APP_EBAY_CLIENT_ID ? "bg-gray-100" : ""
                } focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                disabled={!!process.env.REACT_APP_EBAY_CLIENT_ID}
                title={
                  process.env.REACT_APP_EBAY_CLIENT_ID
                    ? "Using Client ID from environment variables"
                    : "Enter your eBay Client ID"
                }
              />
              {process.env.REACT_APP_EBAY_CLIENT_ID && (
                <p className="text-xs text-gray-500 mt-1">
                  Using Client ID from environment variables
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Client Secret
              </label>
              <input
                type="password"
                value={clientSecret}
                onChange={(e) => setClientSecret(e.target.value)}
                placeholder="Your eBay Client Secret"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                RuName
              </label>
              <input
                type="text"
                value={ruName}
                onChange={(e) => setRuName(e.target.value)}
                placeholder="Your eBay RuName"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Dev ID
              </label>
              <input
                type="text"
                value={devId}
                onChange={(e) => setDevId(e.target.value)}
                placeholder="Your eBay Dev ID"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Cert ID
              </label>
              <input
                type="text"
                value={certId}
                onChange={(e) => setCertId(e.target.value)}
                placeholder="Your eBay Cert ID"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          <div className="flex gap-4">
            <button
              onClick={handleAuthorize}
              disabled={isAuthorizing}
              className={`px-4 py-2 rounded-lg text-white font-medium ${
                isAuthorizing
                  ? "bg-blue-400 cursor-not-allowed"
                  : "bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              }`}
            >
              {isAuthorizing ? "Authorizing..." : "Authorize with eBay"}
            </button>
            <button
              onClick={handleGetToken}
              disabled={isLoading}
              className={`px-4 py-2 rounded-lg text-white font-medium ${
                isLoading
                  ? "bg-green-400 cursor-not-allowed"
                  : "bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
              }`}
            >
              {isLoading ? "Getting Token..." : "Get Token"}
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Refresh Token
            </label>
            <input
              type="text"
              value={refreshToken}
              onChange={(e) => setRefreshToken(e.target.value)}
              placeholder="Your eBay Refresh Token"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <button
            onClick={handleRefreshToken}
            disabled={isLoading}
            className={`px-4 py-2 rounded-lg text-white font-medium ${
              isLoading
                ? "bg-blue-400 cursor-not-allowed"
                : "bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            }`}
          >
            {isLoading ? "Refreshing..." : "Refresh Token"}
          </button>
        </div>
      )}

      {tokenInfo && (
        <div className="mt-8 bg-gray-50 p-6 rounded-lg">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Token Information
          </h3>
          <div className="space-y-2 text-sm text-gray-600">
            <p>
              <span className="font-medium">Access Token:</span>{" "}
              {tokenInfo.access_token.substring(0, 20)}...
            </p>
            <p>
              <span className="font-medium">Token Type:</span>{" "}
              {tokenInfo.token_type}
            </p>
            <p>
              <span className="font-medium">Expires In:</span>{" "}
              {tokenInfo.expires_in} seconds
            </p>
            {tokenInfo.refresh_token && (
              <p>
                <span className="font-medium">Refresh Token:</span>{" "}
                {tokenInfo.refresh_token.substring(0, 20)}...
              </p>
            )}
          </div>
          <div className="mt-4 bg-gray-100 p-4 rounded-lg overflow-x-auto">
            <pre className="text-xs">{JSON.stringify(tokenInfo, null, 2)}</pre>
          </div>
        </div>
      )}
    </div>
  );
}

export default ClientTokenGenerator;
